import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { accountModeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  Users, Heart, Briefcase, Home, User, Crown, 
  RefreshCw, ChevronRight, UserPlus, Trash2, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Input } from './ui/input';

const MODES = {
  particular: {
    label: 'Particular',
    description: 'Uso individual (siempre activo)',
    icon: User,
    color: 'text-slate-500',
    bgColor: 'bg-slate-100 dark:bg-slate-800',
    maxMembers: 1,
    canDisable: false
  },
  love: {
    label: 'Pareja (Love)',
    description: '2 usuarios exactos para finanzas compartidas',
    icon: Heart,
    color: 'text-pink-500',
    bgColor: 'bg-pink-50 dark:bg-pink-950',
    maxMembers: 2,
    canDisable: true
  },
  team: {
    label: 'Team',
    description: 'Hasta 4-8 miembros para grupos o compañeros',
    icon: Briefcase,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50 dark:bg-blue-950',
    maxMembers: 4,
    canDisable: true
  },
  family: {
    label: 'Familia',
    description: 'Gestión financiera del hogar familiar',
    icon: Home,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950',
    maxMembers: 4,
    canDisable: true
  }
};

const AccountModeSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [modeData, setModeData] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    loadModeData();
  }, []);

  const loadModeData = async () => {
    try {
      const res = await accountModeAPI.getMode();
      setModeData(res.data);
    } catch (error) {
      console.error('Failed to load account mode');
    } finally {
      setLoading(false);
    }
  };

  const handleModeToggle = async (mode, enabled) => {
    if (mode === 'particular') return; // Can't toggle particular
    
    setUpdating(true);
    try {
      await accountModeAPI.updateMode({ mode, enabled });
      toast.success(enabled ? `Modo ${MODES[mode].label} activado` : 'Volviendo a modo Particular');
      loadModeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar modo');
    } finally {
      setUpdating(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Introduce un email');
      return;
    }

    setInviting(true);
    try {
      await accountModeAPI.inviteMember(inviteEmail);
      toast.success(`Invitación enviada a ${inviteEmail}`);
      setInviteEmail('');
      loadModeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al invitar');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (userId, memberName) => {
    if (!window.confirm(`¿Eliminar a ${memberName} del grupo?`)) return;
    
    try {
      await accountModeAPI.removeMember(userId);
      toast.success('Miembro eliminado');
      loadModeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (loading) {
    return (
      <Card className="border-border rounded-sm shadow-none">
        <CardContent className="p-6 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  const currentMode = modeData?.current_mode || 'particular';
  const isOwner = modeData?.members?.some(m => m.user_id === user?.id && m.role === 'owner');

  return (
    <>
      <Card className="border-border rounded-sm shadow-none" data-testid="account-mode-card">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Users size={18} className="text-primary" />
            Modalidad de Cuenta
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Mode Selection */}
          {Object.entries(MODES).map(([modeKey, modeInfo]) => {
            const Icon = modeInfo.icon;
            const isActive = currentMode === modeKey;
            const isParticular = modeKey === 'particular';
            
            return (
              <div 
                key={modeKey}
                className={cn(
                  "flex items-center justify-between p-4 rounded-sm border transition-all",
                  isActive ? "border-primary bg-primary/5" : "border-border",
                  isParticular && "opacity-80"
                )}
              >
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "w-10 h-10 rounded-sm flex items-center justify-center",
                    modeInfo.bgColor
                  )}>
                    <Icon size={20} className={modeInfo.color} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground text-sm">
                        {modeInfo.label}
                      </span>
                      {isActive && !isParticular && (
                        <span className="text-xs px-2 py-0.5 bg-primary text-primary-foreground rounded-full">
                          Activo
                        </span>
                      )}
                      {isParticular && (
                        <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-full">
                          Base
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {modeInfo.description}
                    </p>
                    {isActive && !isParticular && modeData && (
                      <p className="text-xs text-primary mt-1">
                        Miembros: {modeData.current_members}/{modeData.max_members}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {!isParticular && (
                    <Switch
                      checked={isActive}
                      onCheckedChange={(checked) => handleModeToggle(modeKey, checked)}
                      disabled={updating || (isActive && currentMode !== modeKey) || (!isOwner && isActive)}
                      data-testid={`mode-switch-${modeKey}`}
                    />
                  )}
                </div>
              </div>
            );
          })}

          {/* Manage Members Button */}
          {currentMode !== 'particular' && (
            <Button 
              variant="outline" 
              onClick={() => setShowMembers(true)}
              className="w-full rounded-sm mt-4"
              data-testid="manage-members-btn"
            >
              <Users size={16} className="mr-2" />
              Gestionar Miembros
              <ChevronRight size={16} className="ml-auto" />
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Members Dialog */}
      <Dialog open={showMembers} onOpenChange={setShowMembers}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users size={20} className="text-primary" />
              Gestionar Miembros
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Plan Limit Info */}
            <div className="p-3 bg-muted/50 rounded-sm">
              <p className="text-xs text-muted-foreground">
                Tu plan permite hasta <span className="font-semibold text-foreground">{modeData?.max_members} miembros</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Miembros actuales: {modeData?.current_members}/{modeData?.max_members}
              </p>
            </div>

            {/* Members List */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Miembros del Grupo
              </Label>
              {modeData?.members?.map((member, idx) => {
                const isCurrentUser = member.user_id === user?.id;
                const isMemberOwner = member.role === 'owner';
                
                return (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 rounded-sm bg-card border border-border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-semibold text-sm">
                          {member.name?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-foreground text-sm">{member.name}</p>
                          {isCurrentUser && (
                            <span className="text-xs text-muted-foreground">(Tú)</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {isMemberOwner ? (
                        <div className="flex items-center gap-1 text-amber-500">
                          <Crown size={14} />
                          <span className="text-xs font-medium">Propietario</span>
                        </div>
                      ) : (
                        <>
                          <span className="text-xs text-muted-foreground">Miembro</span>
                          {isOwner && !isCurrentUser && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveMember(member.user_id, member.name)}
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Invite Member */}
            {isOwner && modeData?.can_invite && (
              <div className="space-y-2 pt-2 border-t border-border">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                  Invitar Nuevo Miembro
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="email@ejemplo.com"
                      className="rounded-sm pl-9"
                    />
                  </div>
                  <Button
                    onClick={handleInvite}
                    disabled={inviting || !inviteEmail.trim()}
                    className="bg-primary hover:bg-primary/90 rounded-sm"
                  >
                    {inviting ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <UserPlus size={16} />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  El usuario debe tener cuenta en Trevor Finance
                </p>
              </div>
            )}

            {/* Limit Reached Warning */}
            {!modeData?.can_invite && currentMode !== 'particular' && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-sm">
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Has alcanzado el límite de miembros de tu plan. Actualiza para ampliar.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMembers(false)} className="rounded-sm">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AccountModeSettings;
